/**
 * Admin Health / Analytics API hooks.
 *
 * Backed by the standalone `analytics-query` Cloudflare Worker
 * (`api.ohear.ai/_analytics/*`) — NOT by the main Fastify API. The worker
 * verifies the same Supabase JWT we already pass on every request, then
 * runs read-only SQL against the `client_errors` Workers Analytics Engine
 * dataset. Since the worker is mounted on the same `api.ohear.ai` host the
 * SPA already calls, the existing `api()` wrapper Just Works — same auth
 * header, same CORS posture.
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '../api'

export type ClientErrorRange = '24h' | '7d' | '30d'

export interface ClientErrorBucket {
  type: string
  count: number
}

export interface ClientErrorOrg {
  org_id: string
  count: number
}

export interface ClientErrorMessage {
  message: string
  count: number
}

export interface ClientErrorStats {
  range: ClientErrorRange
  org_id: string | null
  generated_at: string
  total: number
  by_type: ClientErrorBucket[]
  by_org: ClientErrorOrg[]
  top_messages: ClientErrorMessage[]
}

export interface ClientErrorStatsArgs {
  range?: ClientErrorRange
  orgId?: string
}

const adminHealthKeys = {
  clientErrorStats: (a: ClientErrorStatsArgs) =>
    ['admin', 'health', 'client-errors', a.range ?? '24h', a.orgId ?? 'all'] as const,
}

export const adminHealthQueryKeys = adminHealthKeys

function qs(params: Record<string, string | undefined>): string {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (!v) continue
    sp.set(k, v)
  }
  const out = sp.toString()
  return out ? `?${out}` : ''
}

/**
 * Fetch aggregated client-error stats from the analytics worker. Cached
 * for 30s so flipping range/org filters feels snappy without hammering CF.
 */
export function useClientErrorStats(args: ClientErrorStatsArgs = {}) {
  const { range = '24h', orgId } = args
  return useQuery({
    queryKey: adminHealthKeys.clientErrorStats({ range, orgId }),
    queryFn: () =>
      api<ClientErrorStats>(`/_analytics/client-errors${qs({ range, org_id: orgId })}`),
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  })
}
